import { fromLexicalJSON } from '@/lib/lexical-utils';
import type {
  CalendarUserAllTasksResponse,
  InvitedEvent,
  ProjectDeadline,
  UserMilestoneTask,
  UserPersonalTask,
} from '@/types';
import { format } from 'date-fns';

export type CalendarEventType = 'personal' | 'milestone' | 'project' | 'invited';

export type CalendarSourceEvent =
  | UserPersonalTask
  | UserMilestoneTask
  | ProjectDeadline
  | InvitedEvent;

export interface CalendarEventItem {
  id: number;
  type: CalendarEventType;
  title: string;
  description: string;
  startDate: string | null;
  endDate: string | null;
  meetingLink?: string | null;
  createdAt?: string | null;
  source: CalendarSourceEvent;
}

type EventTypeMeta = {
  label: string;
  dotClassName: string;
  badgeClassName: string;
};

export const CALENDAR_EVENT_META: Record<CalendarEventType, EventTypeMeta> = {
  personal: {
    label: 'Personal Task',
    dotClassName: 'bg-sky-500',
    badgeClassName:
      'bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-900/50 dark:text-sky-200 dark:border-sky-800',
  },
  milestone: {
    label: 'Milestone',
    dotClassName: 'bg-emerald-500',
    badgeClassName:
      'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/50 dark:text-emerald-200 dark:border-emerald-800',
  },
  project: {
    label: 'Project Deadline',
    dotClassName: 'bg-amber-500',
    badgeClassName:
      'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/50 dark:text-amber-200 dark:border-amber-800',
  },
  invited: {
    label: 'Invited Event',
    dotClassName: 'bg-fuchsia-500',
    badgeClassName:
      'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200 dark:bg-fuchsia-900/50 dark:text-fuchsia-200 dark:border-fuchsia-800',
  },
};

export const normalizeCalendarEvents = (
  response?: CalendarUserAllTasksResponse
): CalendarEventItem[] => {
  if (!response) return [];

  const personalEvents: CalendarEventItem[] = response.user_personal_tasks.map(
    (event) => ({
      id: event.id,
      type: 'personal',
      title: event.title,
      description: fromLexicalJSON(event.description ?? ''),
      meetingLink: event.meeting_link,
      startDate: event.start_date,
      endDate: event.end_date,
      createdAt: event.created_at,
      source: event,
    })
  );

  const milestoneEvents: CalendarEventItem[] =
    response.user_milestones_tasks.map((event) => ({
      id: event.id,
      type: 'milestone',
      title: event.title,
      description: event.milestones ?? '',
      startDate: event.start_date,
      endDate: event.end_date,
      source: event,
    }));

  const projectEvents: CalendarEventItem[] = response.project_deadlines.map(
    (event) => ({
      id: event.id,
      type: 'project',
      title: event.title,
      description: `Project status: ${event.project_status_display}`,
      startDate: event.start_date,
      endDate: event.end_date,
      createdAt: event.createdAt,
      source: event,
    })
  );

  const invitedEvents: CalendarEventItem[] = response.invited_events.map(
    (event) => ({
      id: event.id,
      type: 'invited',
      title: event.title,
      description: fromLexicalJSON(event.description ?? ''),
      meetingLink: event.meeting_link,
      startDate: event.start_date,
      endDate: event.end_date,
      createdAt: event.created_at,
      source: event,
    })
  );

  return [...personalEvents, ...milestoneEvents, ...projectEvents, ...invitedEvents];
};

export const getEventDate = (event: CalendarEventItem) => {
  const value = event.startDate || event.endDate;
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatEventDateRange = (
  startDate: string | null,
  endDate: string | null
) => {
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;

  if (start && !Number.isNaN(start.getTime()) && end && !Number.isNaN(end.getTime())) {
    if (format(start, 'yyyy-MM-dd') === format(end, 'yyyy-MM-dd')) {
      return `${format(start, 'MMM d, yyyy h:mm a')} - ${format(end, 'h:mm a')}`;
    }
    return `${format(start, 'MMM d, yyyy h:mm a')} - ${format(end, 'MMM d, yyyy h:mm a')}`;
  }

  if (start && !Number.isNaN(start.getTime())) return format(start, 'MMM d, yyyy h:mm a');
  if (end && !Number.isNaN(end.getTime())) return format(end, 'MMM d, yyyy h:mm a');
  return 'No date set';
};
